/**
 * A single validation problem — the renderer-agnostic result the whole framework
 * speaks (form-framework validation model). Client validators AND server @validate
 * failures both produce ValidationIssue[]; the renderer decides how to display them.
 *
 * `fields` targeting:
 *   - undefined / []  -> form-level (shown in the banner)
 *   - one name        -> field-level (shown on that field)
 *   - many names      -> multi-field (shown on each, + banner)
 */
export type ValidationIssue = {
  message: string;
  severity: "error" | "warning" | "info";
  fields?: string[];
  /** machine id, e.g. the cube rule name — for traceability */
  code?: string;
};

/** A whole-form validator: pure (values) -> issues. Runs on submit (and on change
 *  if a renderer wants live feedback — same call, re-validates everything). */
export type FormValidator = (values: Record<string, unknown>) => ValidationIssue[];

export const hasBlockingIssue = (issues: ValidationIssue[]): boolean =>
  issues.some((i) => i.severity === "error");

/** issues that target a specific field (field-level or multi-field) */
export const issuesForField = (issues: ValidationIssue[], name: string): ValidationIssue[] =>
  issues.filter((i) => (i.fields ?? []).includes(name));

/** issues with no field target — shown at form level */
export const formLevelIssues = (issues: ValidationIssue[]): ValidationIssue[] =>
  issues.filter((i) => !i.fields || i.fields.length === 0);
